import React, { useContext, useEffect, useMemo } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { AuthContext } from "../contexts/AuthContext";
import { getHelpContent, normalizeHelpLocale } from "../help/content";
import { resolveHelpPersona } from "../help/personas";

export function HelpCenter() {
  const { isPlatformAdmin, canManageCompanyUsers, canCertify, companyRole } =
    useContext(AuthContext);
  const { i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();

  const persona = useMemo(
    () =>
      resolveHelpPersona({
        isPlatformAdmin,
        canManageCompanyUsers,
        canCertify,
        companyRole,
      }),
    [isPlatformAdmin, canManageCompanyUsers, canCertify, companyRole]
  );

  const locale = useMemo(
    () => normalizeHelpLocale(i18n.resolvedLanguage || i18n.language),
    [i18n.resolvedLanguage, i18n.language]
  );

  const content = useMemo(() => getHelpContent(locale, persona), [locale, persona]);
  const sections = Array.isArray(content?.sections) ? content.sections : [];
  const contactEmail = content?.contact?.email || "support@numistrail.com";
  const contactLead = content?.contact?.lead || "Need help?";
  const sectionIds = useMemo(
    () => sections.map((section) => section.id).filter(Boolean),
    [sections]
  );
  const sectionIdSet = useMemo(() => new Set(sectionIds), [sectionIds]);
  const defaultSectionId = sectionIds[0] || "";

  useEffect(() => {
    if (!defaultSectionId) {
      return;
    }
    const hashId = parseHashId(location.hash);
    if (hashId && sectionIdSet.has(hashId)) {
      return;
    }
    navigate(`/help#${defaultSectionId}`, { replace: true });
  }, [defaultSectionId, location.hash, navigate, sectionIdSet]);

  return (
    <div className="legal-page">
      <article className="legal-content">
        <h1>{content.title}</h1>
        <p className="legal-updated">{content.subtitle}</p>
        <p className="legal-updated">
          {content.last_updated_label}: {content.last_updated}
        </p>

        {sections.length > 0 ? (
          <>
            <h2>{content.toc_title}</h2>
            <ul className="help-anchor-list">
              {sections.map((section) => (
                <li key={section.id}>
                  <a href={`#${section.id}`}>{section.title}</a>
                </li>
              ))}
            </ul>
          </>
        ) : null}

        {sections.map((section) => (
          <section key={section.id} id={section.id}>
            <h2>{section.title}</h2>
            <ol className="legal-list">
              {(section.items || []).map((item, index) => (
                <li key={`${section.id}-${index}`}>
                  <p className="help-question">
                    <strong>{item.q}</strong>
                  </p>
                  <p>{item.a}</p>
                </li>
              ))}
            </ol>
          </section>
        ))}

        <p className="help-contact">
          {contactLead} <a href={`mailto:${contactEmail}`}>{contactEmail}</a>
        </p>
      </article>
    </div>
  );
}

function parseHashId(hashValue) {
  if (!hashValue || hashValue.length < 2) {
    return "";
  }
  try {
    return decodeURIComponent(hashValue.slice(1));
  } catch {
    return hashValue.slice(1);
  }
}
